import { Dialog } from "@dash/dialog";
import { Button, TextField } from "@dash/form";
import { useTheme } from "@emotion/react";
import { useState } from "react";
import { MdPushPin } from "react-icons/md";

import { pinnedLinkLimit, pinnedLinkNameMaxLength } from "@megaron/docs-contracts";

type Props = {
  uri: string;
  initialName: string;
  isEditing?: boolean;
  isSaving?: boolean;
  isRemoving?: boolean;
  onSave: (input: { name: string }) => Promise<void>;
  onRemove?: () => Promise<void>;
  onClose: () => void;
};

const errorMessages: Record<string, string> = {
  PinLimitReached: `Możesz przypiąć maksymalnie ${pinnedLinkLimit} linków.`,
  NameEmpty: "Nazwa nie może być pusta.",
  UriEmpty: "Brak adresu strony.",
  NameTooLong: `Nazwa może mieć maksymalnie ${pinnedLinkNameMaxLength} znaków.`,
  UriTooLong: "Adres strony jest za długi.",
};

export const PinnedLinkDialog: React.FC<Props> = ({
  uri,
  initialName,
  isEditing,
  isSaving,
  isRemoving,
  onSave,
  onRemove,
  onClose,
}) => {
  const theme = useTheme();

  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) {
      setError(errorMessages["NameEmpty"]);
      return;
    }

    try {
      await onSave({ name: name.trim() });
      onClose();
    } catch (e) {
      setError(errorMessages[String(e)] ?? "Nie udało się zapisać linku.");
    }
  };

  const handleRemove = async () => {
    if (!onRemove) return;

    try {
      await onRemove();
      onClose();
    } catch (e) {
      setError("Nie udało się odpiąć linku.");
    }
  };

  return (
    <Dialog
      onClose={onClose}
      header={isEditing ? "Edytuj przypięty link" : "Przypnij stronę"}
      placement="center"
      zIndex={1100}
      css={{ width: "100%", maxWidth: "400px" }}
    >
      <div css={{ display: "flex", flexDirection: "column", gap: "0.625rem" }}>
        <TextField
          label="Nazwa"
          value={name}
          onChange={(value) => {
            setName(value);
            setError(null);
          }}
          autoFocus
          error={error}
          inputElementProps={{
            maxLength: pinnedLinkNameMaxLength,
            onKeyDown: (event) => {
              if (event.key === "Enter") handleSave();
            },
          }}
        />

        <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span css={{ fontSize: "0.75rem", opacity: 0.6 }}>Adres</span>

          <span
            title={uri}
            css={{
              fontSize: "0.8125rem",
              color: theme.colors.primary,
              wordBreak: "break-all",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {uri}
          </span>
        </div>

        <div css={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.375rem" }}>
          {isEditing ? (
            <>
              <Button onClick={handleSave} isLoading={isSaving} icon={<MdPushPin />}>
                Zapisz
              </Button>

              {onRemove && (
                <Button variant="outline" color="danger" onClick={handleRemove} isLoading={isRemoving}>
                  Odepnij
                </Button>
              )}

              <Button variant="outline" onClick={onClose} css={{ marginLeft: "auto" }}>
                Anuluj
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} css={{ marginLeft: "auto" }}>
                Anuluj
              </Button>

              <Button onClick={handleSave} isLoading={isSaving} icon={<MdPushPin />}>
                Przypnij
              </Button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};
